/**
 * Image compression utilities for reducing file size before upload.
 */

/**
 * Compresses an image file to a target size (in bytes).
 * Uses canvas to resize and compress the image while maintaining aspect ratio.
 * 
 * @param file - The image file to compress
 * @param maxSizeBytes - Maximum file size in bytes (default: 500KB)
 * @param maxWidth - Maximum width in pixels (default: 1920)
 * @param maxHeight - Maximum height in pixels (default: 1920)
 * @returns Promise with compressed File object
 */
export const compressImage = async (
  file: File,
  maxSizeBytes: number = 500 * 1024, // 500KB default
  maxWidth: number = 1920,
  maxHeight: number = 1920
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Normalize output format: keep PNG (may have transparency), convert everything else
        // (HEIC, WebP, BMP, TIFF, etc.) to JPEG for maximum browser/server compatibility.
        // This also normalises the file extension to lowercase, preventing case-sensitivity
        // issues on Linux where "uuid.HEIC" and "uuid.heic" are different files.
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const outputExtension = outputType === 'image/png' ? '.png' : '.jpg';
        const baseName = file.name.replace(/\.[^.]*$/, '') || 'profile';
        const outputFilename = baseName + outputExtension;

        // Try different quality levels to get under maxSizeBytes
        // Start with 0.9 and reduce by 0.15 each iteration until we're under the limit
        const tryCompress = (quality: number): void => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              
              // If blob is already under max size, or quality is at minimum, use it
              if (blob.size <= maxSizeBytes || quality <= 0.1) {
                const compressedFile = new File([blob], outputFilename, {
                  type: outputType,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                // Try lower quality (reduce by 0.15 each time for faster convergence, minimum 0.1)
                tryCompress(Math.max(0.1, quality - 0.15));
              }
            },
            outputType,
            quality
          );
        };
        
        // Start with quality 0.9 (90%)
        tryCompress(0.9);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      if (e.target?.result) {
        img.src = e.target.result as string;
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Validates if a file is an image.
 * 
 * @param file - File to validate
 * @returns true if file is an image, false otherwise
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

/**
 * Validates image file size.
 * 
 * @param file - File to validate
 * @param maxSizeBytes - Maximum file size in bytes (default: 5MB)
 * @returns true if file size is valid, false otherwise
 */
export const isValidImageSize = (file: File, maxSizeBytes: number = 5 * 1024 * 1024): boolean => {
  return file.size <= maxSizeBytes;
};
